import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  TrendingUp, Shield, Cpu, Users, MapPin, Rocket,
  Zap, Phone, Award, Target, Lock, Unlock, CheckCircle2, Star, Quote, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';

/**
 * Careers / Recruitment landing page.
 * Public sections: hero, tech stack, culture, onboarding funnel.
 * Fee structure is GATED — agents must submit name/email/phone/brokerage
 * before the Bespoke Collaboration Plan is revealed. Prevents the general
 * public (clients) from seeing internal splits.
 */

const TECH_STACK = [
  { icon: Globe, name: 'Your Branded Website', label: 'Live at /agents/your-name', body: 'Your own page with photo, bio, specialties, and your active MLS listings — leads submitted on your page come straight to you, not the general pool.' },
  { icon: Zap, name: 'AURI', label: 'AI Transaction Coordinator', body: 'Chases every signature, inspection, and deadline so you can list and show — not shuffle paperwork.' },
  { icon: Cpu, name: 'Nova', label: 'Public AI Concierge', body: 'Qualifies site visitors 24/7 and routes warm buyer/seller inquiries directly to your pipeline.' },
  { icon: MapPin, name: 'Live MLS Search', label: 'TN + MS unified feed', body: 'Full IDX across Memphis MLS and NW MS Realtors — no gated listings, no third-party redirects.' },
  { icon: Target, name: 'Lead Routing CRM', label: 'Round-robin + geo-match', body: 'Every inbound lead is attributed, scored, and auto-assigned. Nothing falls through the cracks.' },
  { icon: Phone, name: '10DLC SMS + Email', label: 'Carrier-compliant nurture', body: 'Automated nurture sequences and compliant SMS follow-up fire the moment a lead comes in.' },
  { icon: Shield, name: 'Property + HOA Platform', label: 'Built-in referral revenue', body: 'Refer rental owners and HOA boards into our in-house management arm and earn ongoing referral fees.' },
];

const CULTURE_PILLARS = [
  { icon: Users, title: 'Boutique, not back-office', body: "You get a name, a phone number, and a Principal Broker who actually answers. No cattle calls." },
  { icon: TrendingUp, title: 'Grow at your pace', body: 'From first license to team leader — the structure flexes with you. No arbitrary production minimums to get started.' },
  { icon: Award, title: 'Built on faith & excellence', body: 'Our standard: show up on time, do what you said, and treat every client like family. Full stop.' },
];

const FEE_PERSIDE = [
  { range: 'Under $100,000', fee: '10% of GCI' },
  { range: '$100,000 – $249,999', fee: '$395' },
  { range: '$250,000 – $499,999', fee: '$595' },
  { range: '$500,000 – $999,999', fee: '$795' },
  { range: '$1 Million +', fee: '1/10% of price (.001%)' },
];

const FEE_OTHER = [
  { label: 'Residential Leases', value: 'No fee' },
  { label: 'Referral transactions', value: '10% of fee received' },
  { label: 'Owner / Agent transactions', value: '$495 per transaction' },
];

const ONBOARDING_INCLUDED = [
  '100 Business Cards',
  'Bespoke Merch',
  'Name Tag',
  '2 Sign Riders',
  '6 Weeks Training',
];

const Careers = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', current_brokerage: '', years_licensed: '', license_state: '',
  });
  const [aotm, setAotm] = useState(null);
  const [agentPhotos, setAgentPhotos] = useState({});

  useEffect(() => {
    api.get('/careers/agent-of-the-month')
      .then(({ data }) => setAotm(data?.agent || null))
      .catch(() => {});
    api.get('/agent-sites/public/list')
      .then(({ data }) => {
        const map = {};
        (Array.isArray(data) ? data : []).forEach((a) => {
          if (a?.slug) map[a.slug] = a.photo;
        });
        setAgentPhotos(map);
      })
      .catch(() => {});
  }, []);

  // Real quotes from current Bespoke Realty agents.
  // `photo` acts as an override — if set, we use it; otherwise we look up the
  // slug in the agent_sites photo map; otherwise we fall back to initials.
  const TESTIMONIALS = [
    {
      slug: 'monique-reed',
      name: 'Monique Reed',
      role: 'Agent · Bespoke Realty',
      photo: 'https://customer-assets.emergentagent.com/job_property-finder-378/artifacts/a4h49ihm_Untitled%20design%2831%29.png',
      quote: "Being an agent at Bespoke Realty guarantees the only person you can blame if your real estate business is not thriving is yourself. Having not one but two responsive brokers who provide training and resources requested by the agents, makes running your real estate business seamless. The best perk of partnering with Bespoke Realty is there's no drama — everyone with good intentions is welcomed and treated like family.",
    },
    {
      slug: 'sharon-thornton',
      name: 'Sharon Thornton',
      role: 'Agent · Bespoke Realty',
      quote: "What makes our brokerage special is the people — from leadership to our agents. We support one another, share our knowledge and our strengths, and genuinely care about giving our best to our clients and the community we call home.",
    },
    {
      slug: 'yolanda-gatewood',
      name: 'Yolanda Gatewood',
      role: 'Agent · 4+ years with Bespoke',
      quote: "I've been with Bespoke for over 4 years now. Bespoke is a brokerage that inspires growth, encourages individuality, and provides the support to confidently build a business.",
    },
    {
      slug: 'velinda-humphrey',
      name: 'Velinda Humphrey',
      role: 'Agent · Bespoke Realty',
      quote: "Bespoke Realty supports my growth as a Realtor and gives me the confidence to serve every client with care and compassion.",
    },
  ];

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleReveal = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Name, email, and phone are required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/careers/fee-request', form);
      setUnlocked(true);
      toast.success("You're in — the Bespoke Collaboration Plan is below.");
      // Smooth scroll to the revealed section
      setTimeout(() => {
        document.getElementById('fee-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="careers-page">
      <StructuredData schema={[
        schemas.organization(),
        schemas.breadcrumb([{ name: 'Home', url: '/' }, { name: 'Careers', url: '/careers' }]),
      ]} />

      {/* ───── Hero ───── */}
      <section className="relative overflow-hidden bg-[#434345]">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/llmeymjf_Joinourrteam.png)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#434345] via-[#434345]/90 to-[#434345]/60" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#BCA480]/20 border border-[#BCA480]/40 text-[#BCA480] px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase mb-6" data-testid="careers-badge">
              <Rocket className="h-3 w-3" /> Now hiring agents · Memphis · TN · MS · AR
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6" data-testid="careers-hero-heading">
              A 100% brokerage, <br />
              <span className="text-[#BCA480]">built for the agent.</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl leading-relaxed mb-8">
              One flat plan. Real leads from a live MLS platform. In-house AI that chases paperwork so you don't.
              Zero fluff — just the tools and backing serious agents actually need.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="#fee-request">
                <Button
                  size="lg"
                  className="bg-[#BCA480] hover:bg-[#a89470] text-white px-8 h-12 text-base shadow-lg shadow-[#BCA480]/30"
                  data-testid="careers-see-plan-cta"
                >
                  See the Plan →
                </Button>
              </a>
              <Link to="/apply">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/40 text-white bg-white/10 hover:bg-white/20 px-8 h-12 text-base backdrop-blur"
                  data-testid="careers-apply-cta"
                >
                  Apply to Join
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative border-t border-white/10 bg-black/20 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              { k: '100%', v: 'Agent commission plan' },
              { k: '3 States', v: 'TN · MS · AR licensed' },
              { k: '6 Weeks', v: 'Fast Start training' },
              { k: '24/7', v: 'AI lead qualification' },
            ].map((s) => (
              <div key={s.k}>
                <div className="text-2xl lg:text-3xl font-bold text-[#BCA480]">{s.k}</div>
                <div className="text-xs lg:text-sm text-white/70 uppercase tracking-wider mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── Tech Stack ───── */}
      <section className="py-20 lg:py-28 bg-[#434345] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs tracking-widest text-[#BCA480] font-semibold uppercase mb-3">01 · The stack</div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="careers-tech-heading">
              Tech that gives you back weekends.
            </h2>
            <p className="text-lg text-white/70 leading-relaxed">
              Most brokerages sell a brand. We sell <em>systems</em>. Every agent gets the same AI tools, lead routing,
              and compliance infrastructure that used to be reserved for mega-teams.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="careers-tech-grid">
            {TECH_STACK.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.name}
                  className="group bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-[#BCA480]/50 transition-all backdrop-blur-sm"
                  data-testid={`careers-tech-${t.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  <div className="inline-flex items-center justify-center w-11 h-11 bg-[#BCA480]/20 border border-[#BCA480]/40 rounded-lg mb-4 group-hover:bg-[#BCA480]/30 transition-colors">
                    <Icon className="h-5 w-5 text-[#BCA480]" />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-[#BCA480]/80 font-semibold">{t.label}</div>
                  <h3 className="text-xl font-bold text-white mt-1 mb-3">{t.name}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{t.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───── Gated Fee Structure ───── */}
      <section
        id="fee-request"
        className={`py-20 lg:py-28 transition-colors duration-500 ${unlocked ? 'bg-[#2d2d2f] relative overflow-hidden' : ''}`}
      >
        {/* Subtle luxury texture on dark backdrop */}
        {unlocked && (
          <>
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[1px] bg-gradient-to-r from-transparent via-[#BCA480]/50 to-transparent" />
          </>
        )}

        <div className={`relative ${unlocked ? 'max-w-7xl' : 'max-w-5xl'} mx-auto px-4 sm:px-6 lg:px-10`}>
          <div className="text-center mb-14 lg:mb-20">
            <div className="text-xs tracking-[0.4em] text-[#BCA480] font-semibold uppercase mb-4">02 · The Plan</div>
            <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-serif mb-5 ${unlocked ? 'text-white' : 'text-[#434345]'}`} data-testid="careers-plan-heading">
              The Bespoke Collaboration Plan
            </h2>
            <p className={`text-base lg:text-lg leading-relaxed max-w-xl mx-auto ${unlocked ? 'text-white/55' : 'text-gray-600'}`}>
              {unlocked
                ? 'One plan. Flat fee. No tiers you have to climb.'
                : "Our full fee schedule is reserved for licensed agents and new licensees exploring Bespoke. Share a few details and we'll unlock it for you below."}
            </p>
          </div>

          {!unlocked ? (
            // --- Locked state: capture form ----------------------------------
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden ring-1 ring-[#BCA480]/30" data-testid="careers-fee-gate">
              {/* Blurred preview of the plan behind the form */}
              <div className="absolute inset-0 pointer-events-none opacity-20 blur-md select-none" aria-hidden="true">
                <div className="h-full bg-gradient-to-br from-[#434345] via-[#5c5c5f] to-[#BCA480]" />
              </div>

              <div className="relative p-8 sm:p-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#BCA480]/20 flex items-center justify-center">
                    <Lock className="h-5 w-5 text-[#BCA480]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#BCA480] font-semibold">Agents only</div>
                    <div className="text-lg font-bold text-[#434345]">Unlock the Collaboration Plan</div>
                  </div>
                </div>

                <form onSubmit={handleReveal} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="careers-fee-form">
                  <div className="md:col-span-2">
                    <Label htmlFor="name">Full name *</Label>
                    <Input id="name" name="name" value={form.name} onChange={handleChange} required data-testid="careers-form-name" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required data-testid="careers-form-email" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required data-testid="careers-form-phone" />
                  </div>
                  <div>
                    <Label htmlFor="current_brokerage">Current brokerage (if any)</Label>
                    <Input id="current_brokerage" name="current_brokerage" value={form.current_brokerage} onChange={handleChange} placeholder="Independent / Newly licensed" data-testid="careers-form-brokerage" />
                  </div>
                  <div>
                    <Label htmlFor="years_licensed">Years licensed</Label>
                    <Input id="years_licensed" name="years_licensed" value={form.years_licensed} onChange={handleChange} placeholder="e.g. 0, 2, 10+" data-testid="careers-form-years" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="license_state">License state(s)</Label>
                    <Input id="license_state" name="license_state" value={form.license_state} onChange={handleChange} placeholder="TN, MS, AR, or pre-license" data-testid="careers-form-state" />
                  </div>

                  <div className="md:col-span-2">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-12 bg-[#BCA480] hover:bg-[#a89470] text-white font-semibold text-base"
                      data-testid="careers-fee-submit"
                    >
                      {submitting ? 'Unlocking…' : (<><Unlock className="h-4 w-4 mr-2" /> Unlock the Plan</>)}
                    </Button>
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      We'll never sell your info. You'll hear from Benjamin Wright, our Principal Broker, within 24 hours.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            // --- Unlocked state: quiet-luxury asymmetric grid on dark backdrop
            <div id="fee-plan" className="animate-in fade-in duration-700" data-testid="careers-fee-revealed">

              {/* ── Top row · 3 stat cards ── */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                {[
                  { label: 'Monthly', value: '$99', unit: '/ month', hint: 'Flat, no tier upgrades' },
                  { label: 'Admin · Per Transaction', value: '$249', unit: '/ txn', hint: 'Covers compliance + AURI AI' },
                  { label: 'Upfront · One Time', value: '$149', unit: 'sign-on', hint: 'Includes starter kit (below)' },
                ].map((s, i) => (
                  <div
                    key={s.label}
                    className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-8 hover:border-[#BCA480]/40 transition-colors backdrop-blur-sm"
                  >
                    <div className="absolute top-6 right-6 text-[10px] tracking-widest text-[#BCA480]/60 font-mono">{String(i + 1).padStart(2, '0')}</div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[#BCA480] font-semibold mb-4">{s.label}</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl lg:text-6xl font-serif text-white">{s.value}</span>
                      <span className="text-sm text-white/50">{s.unit}</span>
                    </div>
                    <div className="text-xs text-white/40 mt-3 italic">{s.hint}</div>
                  </div>
                ))}
              </div>

              {/* ── 75/25 feature card · full width ── */}
              <div className="relative bg-gradient-to-br from-[#BCA480]/20 via-[#BCA480]/10 to-transparent border border-[#BCA480]/30 rounded-2xl p-10 mb-5 overflow-hidden">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-[#BCA480]/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[#BCA480] font-semibold mb-3">For New Agents</div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-6xl lg:text-7xl font-serif text-white tracking-tight">75</span>
                      <span className="text-3xl text-white/40 font-serif">/</span>
                      <span className="text-6xl lg:text-7xl font-serif text-[#BCA480] tracking-tight">25</span>
                      <span className="text-sm text-white/60 ml-2 pb-2">brokerage split</span>
                    </div>
                  </div>
                  <p className="text-sm text-white/60 italic leading-relaxed md:text-right md:max-w-sm">
                    On your first 5 deals (or $30k GCI, whichever comes first) — then you roll into the standard per-side schedule below.
                  </p>
                </div>
              </div>

              {/* ── Per-side table + What's Included · 7/5 split ── */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-5">
                {/* Per-side table */}
                <div className="lg:col-span-7 bg-white/[0.04] border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <div className="flex items-baseline justify-between mb-6 pb-5 border-b border-white/10">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.3em] text-[#BCA480] font-semibold mb-1">Per Side</div>
                      <div className="text-xs text-white/50">Residential · Land · Commercial</div>
                    </div>
                    <div className="text-[10px] tracking-widest text-[#BCA480]/50 font-mono">04</div>
                  </div>
                  <div className="divide-y divide-white/5">
                    {FEE_PERSIDE.map((row) => (
                      <div key={row.range} className="flex justify-between items-baseline py-3.5 group">
                        <span className="text-sm sm:text-base text-white/80 group-hover:text-white transition-colors">{row.range}</span>
                        <span className="text-lg sm:text-xl font-serif text-[#BCA480]">{row.fee}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What's Included */}
                <div className="lg:col-span-5 bg-white/[0.04] border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <div className="flex items-baseline justify-between mb-6 pb-5 border-b border-white/10">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.3em] text-[#BCA480] font-semibold mb-1">What's Included</div>
                      <div className="text-xs text-white/50">In your sign-on kit</div>
                    </div>
                    <div className="text-[10px] tracking-widest text-[#BCA480]/50 font-mono">05</div>
                  </div>
                  <ul className="space-y-3.5">
                    {ONBOARDING_INCLUDED.map((item) => (
                      <li key={item} className="flex items-center gap-3 text-[15px] text-white/80">
                        <span className="w-1 h-1 bg-[#BCA480] rounded-full flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* ── Also good to know · 3 mini cards ── */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                {FEE_OTHER.map((row, i) => (
                  <div
                    key={row.label}
                    className="bg-white/[0.03] border border-white/10 rounded-xl p-6 hover:border-[#BCA480]/30 transition-colors text-center"
                  >
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#BCA480]/70 font-semibold mb-3">
                      {row.label}
                    </div>
                    <div className="text-xl font-serif text-white">{row.value}</div>
                  </div>
                ))}
              </div>

              {/* ── Referral incentives · full-width feature ── */}
              <div className="relative bg-[#1b1b1d] border border-[#BCA480]/20 rounded-2xl p-10 mb-10 overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#BCA480]/5 rounded-full blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[#BCA480] font-semibold mb-6 text-center">
                    Grow the Team · Lower Your Overhead
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">
                    <div className="text-center md:text-right md:border-r md:border-white/10 md:pr-10">
                      <div className="text-5xl lg:text-6xl font-serif text-white mb-2">3</div>
                      <div className="text-xs text-white/50 uppercase tracking-widest mb-3">Agents in 12 Months</div>
                      <p className="text-sm text-white/70 italic">Monthly fees waived for a year</p>
                    </div>
                    <div className="text-center md:text-left md:pl-10">
                      <div className="text-5xl lg:text-6xl font-serif text-[#BCA480] mb-2">9</div>
                      <div className="text-xs text-white/50 uppercase tracking-widest mb-3">Agents in 12 Months</div>
                      <p className="text-sm text-white/70 italic">
                        Monthly fees waived <span className="text-[#BCA480] font-semibold not-italic">for life</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── CTA ── */}
              <div className="text-center">
                <Link to="/apply">
                  <Button
                    size="lg"
                    className="bg-[#BCA480] hover:bg-[#a89470] text-white px-14 h-14 text-base font-semibold shadow-[0_8px_30px_rgba(188,164,128,0.25)]"
                    data-testid="careers-fee-apply-cta"
                  >
                    Start Your Application →
                  </Button>
                </Link>
                <p className="text-sm text-white/50 mt-5">
                  Questions first?{' '}
                  <Link to="/contact" className="text-[#BCA480] underline underline-offset-4 hover:text-[#d4bf99]">
                    Reach Benjamin directly
                  </Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ───── Culture ───── */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-xs tracking-widest text-[#BCA480] font-semibold uppercase mb-3">03 · The people</div>
              <h2 className="text-4xl sm:text-5xl font-bold text-[#434345] mb-6" data-testid="careers-culture-heading">
                You're not a seat here. You're a name.
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Bespoke is intentionally small. Our Principal Broker, Benjamin Wright, personally meets every new agent
                before they sign. That's not a sales pitch — it's the operating model.
              </p>
              <blockquote className="border-l-4 border-[#BCA480] pl-6 italic text-xl text-[#434345] leading-relaxed">
                "When our agents succeed, our clients succeed, and our communities thrive."
              </blockquote>
              <p className="text-sm text-gray-500 mt-3">— Benjamin &amp; Telesa Wright, Principal Brokers</p>
            </div>

            <div className="space-y-5">
              {CULTURE_PILLARS.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.title}
                    className="flex gap-5 p-6 rounded-xl bg-[#faf8f4] border border-[#BCA480]/20 hover:border-[#BCA480]/50 transition-colors"
                    data-testid={`careers-culture-${c.title.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0,20)}`}
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-[#BCA480] rounded-lg flex items-center justify-center">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#434345] mb-1">{c.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{c.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ───── Agent of the Month ───── */}
      {aotm && (
        <section className="py-16 lg:py-20 bg-[#faf8f4]" data-testid="careers-aotm">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative bg-gradient-to-r from-[#434345] to-[#2d2d2f] rounded-2xl overflow-hidden shadow-2xl">
              <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />
              <div className="relative grid md:grid-cols-[auto,1fr] gap-8 md:gap-10 items-center p-8 sm:p-10">
                {/* Photo */}
                <div className="flex-shrink-0 mx-auto md:mx-0">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-[#BCA480] rounded-full opacity-70 blur-sm" aria-hidden="true" />
                    {aotm.photo ? (
                      <img
                        src={aotm.photo}
                        alt={aotm.name}
                        className="relative w-36 h-36 sm:w-44 sm:h-44 rounded-full object-cover border-4 border-[#BCA480] shadow-xl"
                        data-testid="aotm-photo"
                      />
                    ) : (
                      <div className="relative w-36 h-36 rounded-full bg-[#BCA480] flex items-center justify-center text-white text-5xl font-bold">
                        {aotm.name?.[0] || 'A'}
                      </div>
                    )}
                    <div className="absolute -bottom-2 -right-2 bg-[#BCA480] text-white rounded-full w-11 h-11 flex items-center justify-center shadow-lg">
                      <Star className="h-5 w-5 fill-white" />
                    </div>
                  </div>
                </div>

                {/* Copy */}
                <div className="text-center md:text-left">
                  <div className="text-xs tracking-[0.3em] text-[#BCA480] font-semibold uppercase mb-2">
                    Agent of the Month
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="aotm-name">
                    {aotm.name}
                  </h3>
                  {aotm.stat_line && (
                    <div className="inline-block bg-[#BCA480]/20 border border-[#BCA480]/40 text-[#BCA480] px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase mb-3">
                      {aotm.stat_line}
                    </div>
                  )}
                  {aotm.tagline && (
                    <p className="text-white/80 text-base leading-relaxed mb-5 max-w-xl">
                      "{aotm.tagline}"
                    </p>
                  )}
                  <Link to={aotm.link || '/agents'}>
                    <Button className="bg-[#BCA480] hover:bg-[#a89470] text-white" data-testid="aotm-cta">
                      Meet {aotm.name?.split(' ')[0]} →
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 mt-4">
              Recognized monthly for closings, client reviews, and team contribution.
            </p>
          </div>
        </section>
      )}

      {/* ───── Testimonials ───── */}
      <section className="py-16 lg:py-24 bg-white" data-testid="careers-testimonials">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs tracking-widest text-[#BCA480] font-semibold uppercase mb-3">
              In their own words
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#434345]">
              What our agents actually say.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-7">
            {TESTIMONIALS.map((t, idx) => {
              const photo = t.photo || agentPhotos[t.slug];
              const initials = t.name.split(' ').map(n => n[0]).slice(0, 2).join('');
              return (
                <div
                  key={t.slug}
                  className="relative bg-[#faf8f4] rounded-2xl p-7 lg:p-8 border border-[#BCA480]/20 hover:border-[#BCA480]/50 transition-colors"
                  data-testid={`careers-testimonial-${t.slug}`}
                >
                  <Quote className="h-8 w-8 text-[#BCA480]/70 mb-4" />
                  <p className="text-gray-700 italic text-[15px] leading-relaxed mb-6">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-[#BCA480]/20">
                    {photo ? (
                      <img
                        src={photo}
                        alt={t.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-[#BCA480]/50"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#BCA480] text-white font-bold text-sm flex items-center justify-center border-2 border-[#BCA480]/50">
                        {initials}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-semibold text-[#434345]">{t.name}</div>
                      <div className="text-xs text-gray-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───── Application Funnel ───── */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#BCA480] to-[#a89470] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs tracking-widest text-white/80 font-semibold uppercase mb-3">04 · Next steps</div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6" data-testid="careers-funnel-heading">
            From application to active in 7 days.
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-12 leading-relaxed">
            No long committees, no ghosted applications. We review every form within 48 hours and move fast when it's a fit.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12 text-left">
            {[
              { n: '01', t: 'Apply', b: 'Short form — 3 minutes. Attach your license.' },
              { n: '02', t: 'Chat', b: '20-min call with Benjamin. Zero pressure.' },
              { n: '03', t: 'Onboard', b: 'Transfer your license + MLS + CRM access.' },
              { n: '04', t: 'Launch', b: "Full MLS, CRM, AURI, and Nova active from day one. You're in the lead rotation the moment you're activated." },
            ].map((s) => (
              <div
                key={s.n}
                className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-colors"
                data-testid={`careers-step-${s.n}`}
              >
                <div className="text-3xl font-bold text-white/40">{s.n}</div>
                <div className="text-lg font-bold text-white mt-2">{s.t}</div>
                <div className="text-sm text-white/80 mt-1">{s.b}</div>
              </div>
            ))}
          </div>

          <Link to="/apply">
            <Button
              size="lg"
              className="bg-white text-[#434345] hover:bg-[#faf8f4] px-10 h-14 text-base font-bold shadow-xl"
              data-testid="careers-footer-apply-cta"
            >
              Start Your Application →
            </Button>
          </Link>
          <p className="text-sm text-white/80 mt-6">
            Prefer a conversation first?{' '}
            <Link to="/contact" className="underline font-semibold hover:text-white" data-testid="careers-contact-link">
              Reach Benjamin directly
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  );
};

export default Careers;
